// Market data for a page: from the feed (initial session + streamed bar updates). Out of hours, before
// the broker connects, and for members awaiting approval, the server serves the last real session
// ("closed"). Never simulated. Pages read `session` and `version`.
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { NSE_INDICES, type Bar, type Session } from '@planb/core';
import { api, stream } from '@/lib/api';

export interface FeedStatus {
  /** Signed in but not yet approved: the last closed session, read-only. */
  locked?: boolean;
  offline?: boolean;
  error?: string | null;
  brokerConfigured?: boolean;
  loggedIn?: boolean;
  user?: string | null;
  note?: string | null;
  ready?: boolean;
  phase?: string;
  done?: number;
  total?: number;
  ticker?: string;
  lastTick?: string | null;
  date?: string | null;
  extra?: { phase: string; done: number; total: number };
  catchUp?: { phase: string; done: number; total: number };
  /** The exchange's state from NSE's calendar (the server's, so holidays and Muhurat count). */
  clock?: MarketClockState;
}

export interface MarketClockState {
  state: 'open' | 'pre' | 'closed' | 'weekend' | 'holiday' | 'special';
  open: boolean;
  holiday?: string;
  /** A special session on the holiday (Muhurat trading); open 0 = hours not announced yet. */
  special?: { name: string; open: number; close: number };
  nextOpen?: string;
}

export type Mode = 'sim' | 'loading' | 'live' | 'closed' | 'error';

/** The release switches the signed-in user may use (from /v1/status; the server enforces them). */
export type Features = Record<string, boolean>;
let currentFeatures: Features | null = null;
const featureListeners = new Set<(f: Features) => void>();
function publishFeatures(f: Features) {
  if (currentFeatures && JSON.stringify(currentFeatures) === JSON.stringify(f)) return;
  currentFeatures = f;
  for (const l of featureListeners) l(f);
}
/** The user's switches: null until the first status, then on/off by name (unknown names count as on). */
export function useFeatures() {
  const [f, setF] = useState<Features | null>(currentFeatures);
  useEffect(() => { featureListeners.add(setF); return () => { featureListeners.delete(setF); }; }, []);
  return { ready: f !== null, on: (name: string) => f === null || f[name] !== false };
}

export const todayIso = () => new Date().toISOString().slice(0, 10);

interface StreamBars {
  day: string | null;
  bars: number;
  updates: { symbol: string; idx: number; bar: Bar }[];
  indices: { name: string; ltp: number | null; prevClose: number | null }[];
}

// Live index values from the stream (Kite name → quote), shared by every page's index strip so it
// moves with the ticks instead of waiting for the 15-second poll.
const streamedIndices = new Map<string, { ltp: number | null; prevClose: number | null }>();
let streamedVersion = 0;
const indexSubs = new Set<() => void>();
function publishIndices(list: StreamBars['indices']) {
  let changed = false;
  for (const q of list) {
    const old = streamedIndices.get(q.name);
    if (!old || old.ltp !== q.ltp || old.prevClose !== q.prevClose) { streamedIndices.set(q.name, { ltp: q.ltp, prevClose: q.prevClose }); changed = true; }
  }
  if (changed) { streamedVersion++; indexSubs.forEach((f) => f()); }
}

// The last session per index and the last feed status, kept across pages: a page opens with what was
// last seen (the live stream keeps updating the same session while any page holds it) and refreshes
// it at once in the background, instead of a blank "Loading" on every tab switch.
const sessionCache = new Map<string, Session>();
let statusCache: { status: FeedStatus; mode: Mode; user: string | null; role: 'admin' | 'member' | null } | null = null;

export function useMarket(opts: { index: string; seed: number }) {
  const [session, setSession] = useState<Session | null>(() => sessionCache.get(opts.index) ?? null);
  const [status, setStatus] = useState<FeedStatus | null>(statusCache?.status ?? null);
  const [mode, setMode] = useState<Mode>(statusCache?.mode ?? 'loading');
  const [version, setVersion] = useState(() => (sessionCache.has(opts.index) ? 1 : 0));
  const [user, setUser] = useState<string | null>(statusCache?.user ?? null);
  const [role, setRole] = useState<'admin' | 'member' | null>(statusCache?.role ?? null);
  // Another index picked on the page: its kept session, if any, until the fresh one arrives.
  const shownIndex = useRef(opts.index);
  useEffect(() => {
    if (shownIndex.current === opts.index) return;
    shownIndex.current = opts.index;
    const c = sessionCache.get(opts.index);
    if (c) { setSession(c); setVersion((v) => v + 1); }
  }, [opts.index]);
  const live = mode === 'live' || mode === 'closed';
  const sessionRef = useRef<Session | null>(null);
  sessionRef.current = session;


  // Feed status every 15 s decides live vs simulated.
  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const r = await api('/v1/status');
        if (r.status === 401) return; // api() sends the browser to sign-in
        const { feed, clock, features, user: who, role: rl } = (await r.json()) as { feed: FeedStatus; clock?: MarketClockState; features?: Features; user: string; role: 'admin' | 'member' };
        if (stop) return;
        setStatus({ ...feed, clock });
        if (features) publishFeatures(features);
        setUser(who);
        setRole(rl);
        // Real data whenever the feed has a session: live, or the last one (closed), with or
        // without the broker.
        // Awaiting approval: the last closed session, read-only (the server serves that copy).
        const m: Mode = feed.locked ? (feed.ready ? 'closed' : 'loading')
          : feed.ready ? (feed.phase === 'live' ? 'live' : 'closed')
            : feed.phase === 'error' || feed.offline ? 'error' : 'loading';
        setMode(m);
        statusCache = { status: { ...feed, clock }, mode: m, user: who, role: rl };
      } catch {
        if (stop) return;
        setStatus({ offline: true, error: 'Cannot reach MoneyHQ' });
        // Keep showing what was loaded through a blip; with nothing yet, say so.
        if (!sessionRef.current) setMode('error');
      }
    };
    void poll();
    const id = setInterval(poll, 15_000);
    return () => { stop = true; clearInterval(id); };
  }, []);

  const loadLive = useCallback(async () => {
    const r = await api(`/v1/session?index=${encodeURIComponent(opts.index)}`);
    if (r.ok) { const s = await r.json() as Session; sessionCache.set(opts.index, s); setSession(s); setVersion((v) => v + 1); }
  }, [opts.index]);

  // Live: fetch the session, then apply streamed bar updates (batched to ≤2 redraws/second).
  useEffect(() => {
    if (!live) return;
    void loadLive();
    const es = stream(`/v1/stream?index=${encodeURIComponent(opts.index)}`);
    let pending = false;
    const kite = NSE_INDICES.find((i) => i.id === opts.index)?.kite;
    es.addEventListener('reset', () => { void loadLive(); });
    es.addEventListener('bars', (ev) => {
      const s = sessionRef.current;
      if (!s || s.source !== 'live') return;
      const msg = JSON.parse((ev as MessageEvent).data) as StreamBars;
      publishIndices(msg.indices ?? []);
      if (msg.day && msg.day !== s.date) { void loadLive(); return; }
      const bySym = new Map(s.stocks.map((x) => [x.symbol, x]));
      // New bar index: extend every stock flat, as the feed does, before applying updates.
      for (const st of s.stocks) {
        while (st.bars.length < msg.bars) {
          const prev = st.bars[st.bars.length - 1];
          const c = prev ? prev.c : st.prevClose;
          st.bars.push({ o: c, h: c, l: c, c, v: 0, oi: prev ? prev.oi : st.prevOI });
        }
      }
      for (const u of msg.updates) {
        const st = bySym.get(u.symbol);
        if (st && u.idx < st.bars.length) st.bars[u.idx] = u.bar;
      }
      const q = kite && msg.indices.find((i) => i.name === kite);
      if (q && s.index) { s.index.ltp = q.ltp; s.index.prevClose = q.prevClose; }
      if (!pending) {
        pending = true;
        setTimeout(() => { pending = false; setVersion((v) => v + 1); }, 500);
      }
    });
    return () => es.close();
  }, [live, opts.index, loadLive]);

  return { session, status, mode, live, version, user, role };
}

export interface IndexStripItem { id: string; name: string; ltp: number | null; pct: number | null }

/** Values for the index strip: Kite index quotes when live, members' average move when simulated. */
export function useIndexStrip(session: Session | null, live: boolean, version: number): IndexStripItem[] {
  const [quotes, setQuotes] = useState<{ id: string; name: string; ltp: number | null; prevClose: number | null }[]>([]);
  // Re-render when streamed index values move.
  useSyncExternalStore((f) => { indexSubs.add(f); return () => indexSubs.delete(f); }, () => streamedVersion);
  useEffect(() => {
    if (!live) return;
    let stop = false;
    const load = async () => {
      const r = await api('/v1/indices').catch(() => null);
      if (r?.ok && !stop) setQuotes(await r.json());
    };
    void load();
    const id = setInterval(load, 15_000);
    return () => { stop = true; clearInterval(id); };
  }, [live]);

  if (live && quotes.length) {
    return quotes.map((q) => {
      // The stream's value (keyed by Kite name) is newer than the last poll.
      const kite = NSE_INDICES.find((i) => i.id === q.id)?.kite;
      const s = kite ? streamedIndices.get(kite) : undefined;
      const ltp = s?.ltp ?? q.ltp, prevClose = s?.prevClose ?? q.prevClose;
      return { id: q.id, name: q.name, ltp, pct: ltp && prevClose ? (ltp / prevClose - 1) * 100 : null };
    });
  }
  if (!session) return [];
  void version;
  const pctBy = new Map(session.stocks.map((s) => [s.symbol, s.bars.length ? (s.bars[s.bars.length - 1].c / s.prevClose - 1) * 100 : 0]));
  return NSE_INDICES.filter((i) => i.kite).map((i) => {
    const vals = i.members.map((m) => pctBy.get(m)).filter((v): v is number => v !== undefined);
    return { id: i.id, name: i.name, ltp: null, pct: vals.length ? vals.reduce((a, v) => a + v, 0) / vals.length : null };
  });
}

/** Whether bar t-1 is still trading (live session, market open, showing the latest bar). */
export const lastBarForming = (mode: Mode, t: number, n: number) => mode === 'live' && t > 0 && t === n;
